import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Navbar } from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { Loader2, CreditCard, CheckCircle2, XCircle, Calendar, Hash, Coins } from "lucide-react";
import { CreditTopUp } from "@/components/CreditTopUp";

const STATUS_LABELS = {
  ACTIVE: { text: "Actif", cls: "text-emerald-400 bg-emerald-500/10 border-emerald-500/30" },
  APPROVAL_PENDING: { text: "En attente d'approbation", cls: "text-amber-400 bg-amber-500/10 border-amber-500/30" },
  APPROVED: { text: "Approuvé", cls: "text-emerald-400 bg-emerald-500/10 border-emerald-500/30" },
  SUSPENDED: { text: "Suspendu", cls: "text-amber-400 bg-amber-500/10 border-amber-500/30" },
  CANCELLED: { text: "Annulé", cls: "text-red-400 bg-red-500/10 border-red-500/30" },
  EXPIRED: { text: "Expiré", cls: "text-red-400 bg-red-500/10 border-red-500/30" },
  PAYMENT_FAILED: { text: "Paiement échoué", cls: "text-red-400 bg-red-500/10 border-red-500/30" },
};

function Row({ icon: Icon, label, value, testid }) {
  return (
    <div className="flex items-center justify-between py-3.5 border-b border-white/5">
      <span className="flex items-center gap-2 text-slate-400 text-sm"><Icon className="w-4 h-4" /> {label}</span>
      <span className="font-medium text-sm text-right" data-testid={testid}>{value}</span>
    </div>
  );
}

export default function Subscription() {
  const { refresh } = useAuth();
  const navigate = useNavigate();
  const [sub, setSub] = useState(null);
  const [loading, setLoading] = useState(true);
  const [cancelling, setCancelling] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/subscription");
      setSub(data);
    } catch (e) {} finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const cancel = async () => {
    setCancelling(true);
    try {
      await api.post("/subscription/cancel");
      toast.success("Abonnement annulé.");
      await refresh();
      await load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setCancelling(false);
    }
  };

  const fmtDate = (d) => (d ? new Date(d).toLocaleString("fr-FR", { dateStyle: "long", timeStyle: "short" }) : "—");
  const status = sub?.status ? (STATUS_LABELS[sub.status] || { text: sub.status, cls: "text-slate-300 bg-white/5 border-white/10" }) : null;

  return (
    <div className="studio-bg grain min-h-screen relative">
      <Navbar />
      <main className="relative z-10 max-w-2xl mx-auto px-5 sm:px-8 py-12">
        <h1 className="font-display text-3xl font-extrabold tracking-tight mb-8 fade-up flex items-center gap-2">
          <CreditCard className="w-6 h-6 text-[#3ECF8E]" /> Mon abonnement
        </h1>

        {loading ? (
          <div className="grid place-items-center py-20"><Loader2 className="w-8 h-8 animate-spin text-[#3ECF8E]" /></div>
        ) : !sub || !sub.id ? (
          <div className="glass rounded-3xl p-10 text-center fade-up" data-testid="no-subscription">
            <XCircle className="w-14 h-14 text-slate-500 mx-auto" />
            <h2 className="font-display text-xl font-bold mt-4">Aucun abonnement actif</h2>
            <p className="text-slate-400 mt-2">Souscrivez à un plan pour recevoir des crédits chaque mois.</p>
            <Button onClick={() => navigate("/pricing")} className="mt-6 h-11 px-6 bg-[#3ECF8E] hover:bg-[#2fb879] text-black glow-btn rounded-full font-semibold" data-testid="see-plans-button">
              Voir les plans
            </Button>
          </div>
        ) : (
          <div className="glass rounded-3xl p-7 fade-up" data-testid="subscription-details">
            <div className="flex items-center justify-between mb-4">
              <div>
                <div className="text-xs uppercase tracking-wider text-slate-500">Plan actuel</div>
                <div className="font-display text-2xl font-bold" data-testid="sub-plan-name">{sub.plan_name || "—"}</div>
              </div>
              {status && (
                <span className={`text-xs font-semibold rounded-full px-3 py-1.5 border ${status.cls}`} data-testid="sub-status">
                  {status.text}
                </span>
              )}
            </div>

            <Row icon={Coins} label="Prix" value={`${sub.price} ${sub.currency} / mois`} testid="sub-price" />
            <Row icon={Coins} label="Crédits par cycle" value={`${sub.credits_per_cycle ?? "—"} crédits`} testid="sub-credits" />
            <Row icon={Calendar} label="Prochaine facturation" value={fmtDate(sub.next_billing_time)} testid="sub-next-billing" />
            <Row icon={Hash} label="ID d'abonnement" value={<span className="font-mono text-xs">{sub.id}</span>} testid="sub-id" />

            {sub.active ? (
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button variant="outline" className="w-full mt-6 h-11 rounded-full border-red-500/30 text-red-400 hover:bg-red-500/10 hover:text-red-300" data-testid="cancel-subscription-button">
                    Annuler l'abonnement
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent className="bg-[#12121a] border-white/10">
                  <AlertDialogHeader>
                    <AlertDialogTitle>Annuler votre abonnement ?</AlertDialogTitle>
                    <AlertDialogDescription className="text-slate-400">
                      Vous garderez l'accès jusqu'à la fin de la période déjà payée. Vous ne serez plus facturé ensuite.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel className="bg-white/5 border-white/10">Retour</AlertDialogCancel>
                    <AlertDialogAction onClick={cancel} disabled={cancelling} className="bg-red-600 hover:bg-red-500" data-testid="confirm-cancel-button">
                      {cancelling ? <Loader2 className="w-4 h-4 animate-spin" /> : "Confirmer l'annulation"}
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            ) : (
              <Button onClick={() => navigate("/pricing")} className="w-full mt-6 h-11 bg-[#3ECF8E] hover:bg-[#2fb879] text-black glow-btn rounded-full font-semibold" data-testid="resubscribe-button">
                Se réabonner
              </Button>
            )}
          </div>
        )}

        {sub && sub.active && (
          <div className="mt-6">
            <CreditTopUp />
          </div>
        )}
      </main>
    </div>
  );
}
